import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { UploadService } from '@services/upload.service';
import { take } from 'rxjs';

import { singleOrderDetails } from '../../../../../../../@store/orders/orders.model';

@Component({
  selector: 'ciwc-order-shipment-attachments',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    NgIf,
    NgFor,
    MatProgressSpinner,
  ],
  standalone: true,
  templateUrl: './order-shipment-attachments.component.html',
  styleUrl: './order-shipment-attachments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderShipmentAttachmentsComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  @Input() orderDetails!: singleOrderDetails;
  @Input() viewDisable!: boolean;
  @Input() shipmentIndex!: number;

  private uploadService = inject(UploadService);
  private fb = inject(FormBuilder);

  get attachments(): FormArray {
    return this.parentForm
      .get('shipments')
      ?.get(this.shipmentIndex.toString())
      ?.get('attachments') as FormArray;
  }

  ngOnInit() {
    // Initialize attachments if they don't exist
    const shipmentGroup = this.parentForm
      .get('shipments')
      ?.get(this.shipmentIndex.toString()) as FormGroup;
    if (shipmentGroup && !shipmentGroup.get('attachments')) {
      shipmentGroup.addControl('attachments', this.fb.array([]));
    }

    // If we have existing attachments in orderDetails, populate them
    if (this.orderDetails?.shipments?.[this.shipmentIndex]?.attachments?.length) {
      const existingAttachments = this.orderDetails.shipments[this.shipmentIndex].attachments;
      existingAttachments.forEach((attachment) => {
        this.attachments.push(
          this.fb.group({
            name: [{ value: attachment.attachmentName, disabled: this.viewDisable }],
            document: [{ value: attachment.attachmentDocument, disabled: this.viewDisable }],
            url: [attachment.url],
            isUploading: [false],
            uploadError: [''],
          }),
        );
      });
    }
  }

  addAttachment() {
    this.attachments.push(
      this.fb.group({
        name: [{ value: '', disabled: this.viewDisable }],
        document: [{ value: '', disabled: this.viewDisable }],
        url: [''],
        isUploading: [false],
        uploadError: [''],
      }),
    );
  }

  removeAttachment(index: number) {
    this.attachments.removeAt(index);
  }

  triggerFileInput(index: number) {
    const fileInput = document.getElementById(
      'shipmentFileInput-' + this.shipmentIndex + '-' + index,
    ) as HTMLInputElement;

    if (fileInput) {
      fileInput.click();
    }
  }

  onFileSelected(event: Event, index: number) {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const attachmentGroup = this.attachments.at(index) as FormGroup;

      // Update the form state immediately
      attachmentGroup.patchValue({
        isUploading: true,
        uploadError: '',
      });

      // Force change detection
      attachmentGroup.updateValueAndValidity();

      this.uploadService
        .uploadFile(file)
        .pipe(take(1))
        .subscribe({
          next: (res) => {
            // Update the form state with the response
            attachmentGroup.patchValue({
              document: res.name,
              url: res.url,
              isUploading: false,
              uploadError: '',
            });

            // Force change detection
            attachmentGroup.updateValueAndValidity();
          },
          error: (err) => {
            // Update the form state with the error
            attachmentGroup.patchValue({
              isUploading: false,
              uploadError: 'Failed to upload file. Please try again.',
            });

            // Force change detection
            attachmentGroup.updateValueAndValidity();
          },
        });
    }
  }

  viewAttachment(index: number) {
    const attachment = this.attachments.at(index).value;
    if (attachment.url) {
      window.open(attachment.url, '_blank');
      return;
    }
    const file = attachment.document;
    if (!file) return;
    if (file instanceof File) {
      const blobUrl = URL.createObjectURL(file);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } else if (typeof file === 'string') {
      window.open(file, '_blank');
    }
  }
}
